// Organigramme — shared tree vocabulary (ported from the design
// prototype's org-core.jsx: index, descendants, ancestors, avatars),
// minus its demo dataset: nodes come from /api/org.
import type { EntityDTO, OrgNodeDTO } from '../../api/hooks.js';

export interface OrgIndex {
  byId: Map<string, OrgNodeDTO>;
  byParent: Map<string, string[]>;
  roots: string[];
}

export function orgIndex(nodes: OrgNodeDTO[]): OrgIndex {
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const byParent = new Map<string, string[]>();
  const roots: string[] = [];
  for (const n of nodes) {
    if (n.managerId && byId.has(n.managerId)) {
      const list = byParent.get(n.managerId);
      if (list) list.push(n.id);
      else byParent.set(n.managerId, [n.id]);
    } else {
      roots.push(n.id);
    }
  }
  return { byId, byParent, roots };
}

// Every id below `id` (cycles are impossible server-side, but the
// `seen` guard keeps a corrupt payload from hanging the page).
export function orgDesc(id: string, byParent: Map<string, string[]>, acc = new Set<string>()): Set<string> {
  for (const c of byParent.get(id) ?? []) {
    if (acc.has(c)) continue;
    acc.add(c);
    orgDesc(c, byParent, acc);
  }
  return acc;
}

// The managerial chain above `id`, closest manager last.
export function orgChain(id: string, byId: Map<string, OrgNodeDTO>): OrgNodeDTO[] {
  const out: OrgNodeDTO[] = [];
  let n = byId.get(id);
  const seen = new Set<string>([id]);
  while (n?.managerId && byId.has(n.managerId) && !seen.has(n.managerId)) {
    seen.add(n.managerId);
    n = byId.get(n.managerId)!;
    out.unshift(n);
  }
  return out;
}

export const orgInitials = (name: string) =>
  name.trim().split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]!.toUpperCase()).join('');

export const orgFirstName = (name: string) => name.trim().split(/\s+/)[0] ?? name;

// Entity colour drives every accent on this page (rail groups, card
// rails, hero top border) — the entity IS the grouping here, since the
// fiche has no division field (`department` holds the job title).
export const entColor = (entities: Map<string, EntityDTO>, id: string) =>
  entities.get(id)?.color ?? '#6f6c7d';
export const entShort = (entities: Map<string, EntityDTO>, id: string) =>
  entities.get(id)?.short ?? '—';
export const entName = (entities: Map<string, EntityDTO>, id: string) =>
  entities.get(id)?.name ?? id;

export const softColor = (color: string, pct = 12) =>
  `color-mix(in srgb, ${color} ${pct}%, transparent)`;

export function OrgAvatar({ n, s, color }: { n: OrgNodeDTO; s: number; color: string }) {
  return (
    <div
      className="org-av"
      style={{ width: s, height: s, background: color, fontSize: Math.round(s * 0.34) }}
      aria-hidden="true"
    >
      {orgInitials(n.name)}
    </div>
  );
}

export const orgMatches = (n: OrgNodeDTO, q: string) => {
  const s = q.trim().toLowerCase();
  if (!s) return true;
  return (
    n.name.toLowerCase().includes(s) ||
    (n.role ?? '').toLowerCase().includes(s) ||
    n.code.toLowerCase().includes(s)
  );
};
